import React from "react";

interface Address {
  country: string;
  city: string;
  state: string;
  line_1: string | null;
  line_2: string | null;
  zip_code: string | null;
}

interface InvoiceDetails {
  business_name?: string;
  email?: string;
  address?: Address;
  subscriber?: string;
  current_period_start?: string;
  current_period_end?: string;
}

interface InvoiceDetailsTabProps {
  invoiceDetails: InvoiceDetails;
  onEdit: () => void;
}

const InvoiceDetailsTab: React.FC<InvoiceDetailsTabProps> = ({
  invoiceDetails,
  onEdit,
}) => {
  // Helper function to format address
  const formatAddress = (address: Address | undefined): string => {
    if (!address) return "No address provided";

    const parts = [];

    if (address.line_1) parts.push(address.line_1);
    if (address.line_2) parts.push(address.line_2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zip_code) parts.push(address.zip_code);
    if (address.country) parts.push(address.country);

    return parts.length > 0 ? parts.join(", ") : "No address details available";
  };

  // Helper function to format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Helper function to format billing cycle
  const formatBillingCycle = (
    start: string | undefined,
    end: string | undefined
  ): string => {
    if (!start || !end) return "No billing cycle available";

    const startDate = formatDate(start);
    const endDate = formatDate(end);
    return `${startDate} to ${endDate}`;
  };

  return (
    <div className="relative pt-2 pb-2">
      <button
        className="absolute top-0 right-0 p-2 text-gray-400 hover:text-gray-600"
        onClick={onEdit}
        aria-label="Edit invoice details"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487a2.1 2.1 0 1 1 2.97 2.97L7.5 19.788 3 21l1.212-4.5 12.65-12.013z"
          />
        </svg>
      </button>
      <div className="flex flex-col gap-7 max-w-4xl">
        <div className="flex flex-row items-baseline">
          <span className="text-gray-500 w-56">Business name</span>
          <span className="font-bold text-gray-900 ml-2">
            {invoiceDetails.business_name || "N/A"}
          </span>
        </div>
        <div className="flex flex-row items-baseline">
          <span className="text-gray-500 w-56">Email</span>
          <span className="font-bold text-gray-900 ml-2">
            {invoiceDetails.email || "N/A"}
          </span>
        </div>
        <div className="flex flex-row items-baseline">
          <span className="text-gray-500 w-56">Address</span>
          <span className="font-bold text-gray-900 ml-2">
            {formatAddress(invoiceDetails.address)}
          </span>
        </div>
        <div className="flex flex-row items-baseline">
          <span className="text-gray-500 w-56">Subscriber</span>
          <span className="font-bold text-gray-900 ml-2">
            {invoiceDetails.subscriber || "N/A"}
          </span>
        </div>
        <div className="flex flex-row items-baseline">
          <span className="text-gray-500 w-56">Current billing cycle</span>
          <span className="font-bold text-gray-900 ml-2">
            {formatBillingCycle(
              invoiceDetails.current_period_start,
              invoiceDetails.current_period_end
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsTab;
